import React from 'react';
import './WalmartUploadPopup.css';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function WalmartUploadPopup({
  showWalmartUploadPopup,
  setShowWalmartUploadPopup,
  walmartFileInputRef,
  selectedWalmartFile,
  setSelectedWalmartFile,
  parsedWalmartData,
  setParsedWalmartData,
  uploadWalmartStatus,
  setUploadWalmartStatus,
  handleWalmartFileUpload,
  handleWalmartUploadSubmit,
  uploadWalmartWeek,
  setUploadWalmartWeek,
  uploadWalmartMonth,
  setUploadWalmartMonth,
  uploadWalmartYear,
  setUploadWalmartYear,
  walmartPeriodMode,
  setWalmartPeriodMode
}) {
  const isWeekly = walmartPeriodMode === 'weekly';

  const closeWalmartUploadPopup = () => {
    setShowWalmartUploadPopup(false);
    setSelectedWalmartFile(null);
    setParsedWalmartData([]);
    setUploadWalmartStatus(null);
    setUploadWalmartWeek('');
    setUploadWalmartMonth('');
    setUploadWalmartYear('');
    if (walmartFileInputRef.current) walmartFileInputRef.current.value = '';
  };

  const handlePeriodModeChange = (mode) => {
    setWalmartPeriodMode(mode);
    setParsedWalmartData([]);
    setUploadWalmartStatus(null);
    setUploadWalmartWeek('');
    setUploadWalmartMonth('');
  };

  const isSubmitDisabled =
    !selectedWalmartFile ||
    !uploadWalmartYear ||
    (isWeekly ? !uploadWalmartWeek : !uploadWalmartMonth) ||
    parsedWalmartData.length === 0 ||
    uploadWalmartStatus?.type === 'loading';

  return (
    <>
      {showWalmartUploadPopup && (
        <div className="walmart-popup-overlay">
          <div className="walmart-popup-content">
            <div className="walmart-popup-header">
              <h2>Upload Walmart Sales Data</h2>
              <button className="walmart-close-popup" onClick={closeWalmartUploadPopup}>✕</button>
            </div>

            <div className="walmart-upload-form">

              {/* ── Period mode toggle ── */}
              <div className="walmart-form-group">
                <label>Period Type</label>
                <div className="walmart-mode-buttons">
                  <button
                    className={`walmart-mode-btn ${isWeekly ? 'active' : ''}`}
                    onClick={() => handlePeriodModeChange('weekly')}
                  >
                    Weekly
                  </button>
                  <button
                    className={`walmart-mode-btn ${!isWeekly ? 'active' : ''}`}
                    onClick={() => handlePeriodModeChange('monthly')}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {/* ── File format info ── */}
              <div className="walmart-file-info">
                <h3>File Format Requirements</h3>
                <ul>
                  <li><strong>Required columns:</strong> SKU, Quantity</li>
                  <li><strong>Supported formats:</strong> CSV, Excel (.xlsx, .xls)</li>
                  <li><strong>Auto-detection:</strong> System will automatically find header row</li>
                  <li><strong>File size:</strong> Maximum 10MB</li>
                </ul>
              </div>

              {/* ── Period fields ── */}
              <div className="form-row">
                {isWeekly ? (
                  <div className="walmart-form-group half-width">
                    <label>Week Number *</label>
                    <input
                      type="number" min="1" max="53"
                      value={uploadWalmartWeek}
                      onChange={(e) => setUploadWalmartWeek(e.target.value)}
                      placeholder="Week (1-53)"
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                ) : (
                  <div className="walmart-form-group half-width">
                    <label>Month *</label>
                    <select
                      value={uploadWalmartMonth}
                      onChange={(e) => setUploadWalmartMonth(e.target.value)}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    >
                      <option value="">Select month</option>
                      {MONTHS.map((m, i) => (
                        <option key={i + 1} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="walmart-form-group half-width">
                  <label>Year *</label>
                  <input
                    type="number" min="2020" max="2030"
                    value={uploadWalmartYear}
                    onChange={(e) => setUploadWalmartYear(e.target.value)}
                    placeholder="Year"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                  />
                </div>
              </div>

              {/* ── File picker ── */}
              <div className="walmart-form-group">
                <label>Select File *</label>
                <div className="walmart-file-upload">
                  <input
                    type="file" ref={walmartFileInputRef}
                    accept=".csv,.xlsx,.xls"
                    onChange={handleWalmartFileUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    className="walmart-file-upload-btn"
                    onClick={() => walmartFileInputRef.current.click()}
                  >
                    {selectedWalmartFile ? selectedWalmartFile.name : 'Choose CSV or Excel file'}
                  </button>
                  {selectedWalmartFile && (
                    <span className="walmart-file-size">
                      {(selectedWalmartFile.size / 1024).toFixed(2)} KB
                    </span>
                  )}
                </div>
                <p className="walmart-file-hint">The system will automatically detect the header row with column names</p>
              </div>

              {/* ── Status ── */}
              {uploadWalmartStatus && (
                <div className={`walmart-upload-status ${uploadWalmartStatus.type}`}>
                  {uploadWalmartStatus.type === 'loading' ? `⏳ ${uploadWalmartStatus.message}` : uploadWalmartStatus.message}
                  {uploadWalmartStatus.details && (
                    <div className="walmart-status-details">{uploadWalmartStatus.details}</div>
                  )}
                </div>
              )}

              {/* ── Preview table ── */}
              {parsedWalmartData.length > 0 && (
                <div className="walmart-data-preview">
                  <div className="walmart-preview-header">
                    <h3>Data Preview ({parsedWalmartData.length} rows)</h3>
                    <div className="walmart-preview-stats">
                      <span className="walmart-stat-item">
                        Unique SKUs: {new Set(parsedWalmartData.map(r => r.sku)).size}
                      </span>
                      <span className="walmart-stat-item">
                        Total Quantity: {parsedWalmartData.reduce((s, r) => s + (r.quantity || 0), 0).toLocaleString()}
                      </span>
                      <span className="walmart-stat-item">
                        {isWeekly ? `Week: ${uploadWalmartWeek}` : `Month: ${MONTHS[uploadWalmartMonth - 1] || uploadWalmartMonth}`}, Year: {uploadWalmartYear}
                      </span>
                    </div>
                  </div>
                  <div className="walmart-preview-table-container">
                    <table className="walmart-preview-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>SKU</th>
                          <th>Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedWalmartData.slice(0, 10).map((row, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td className="walmart-sku-cell">{row.sku || 'N/A'}</td>
                            <td className="walmart-number-cell">
                              <span className={row.quantity > 0 ? 'walmart-positive-value' : 'walmart-zero-value'}>
                                {row.quantity || 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsedWalmartData.length > 10 && (
                      <p className="walmart-preview-more">⋮ ... and {parsedWalmartData.length - 10} more rows</p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Buttons ── */}
              <div className="walmart-popup-buttons">
                <button className="walmart-cancel-btn" onClick={closeWalmartUploadPopup}>Cancel</button>
                <button
                  className="walmart-submit-btn"
                  onClick={handleWalmartUploadSubmit}
                  disabled={isSubmitDisabled}
                >
                  {uploadWalmartStatus?.type === 'loading' ? 'Processing...' : 'Upload to Database'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default WalmartUploadPopup;